import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import { Loader } from './components';
import { AdminRoute, DeadEndRoute, GuestRoute, PrivateRoute } from './components/routes';
import { IS_AI_SUGGEST_ENABLED } from './constants/ai';
import { routes } from './constants/routing';
import { DEPT_ADMIN, DISTRIBUTOR, HUBSPOT_APP_INSTALL_ROLES, ORG_ADMIN, SUPER_ADMIN } from './constants/users';
import {
  CheckInboxContainer,
  LinkExpiredContainer,
  LoginContainer,
  ModalContainer,
  VerifyMagicLinkContainer,
} from './containers';
import {
  AddressBook,
  AuthCallback,
  Automations,
  BulkSendManager,
  BusinessReasons,
  Campaigns,
  ClaimLinkTemplatesPage,
  CustomMessageTemplatesPage,
  Dashboard,
  EmailTemplatesPage,
  HubspotAppIntegration,
  Integrations,
  Inventory,
  OneLinkManager,
  Organizations,
  Reports,
  SignatureSetup,
  SuggestGift,
  Templates,
  Users,
} from './pages';
import { selectIsAutomationsEnabled } from './store/selectors/organizations';
import App from './views/App/App';

const CreateSendHost = React.lazy(() => import('./pages/CreateSendHost/CreateSendHost'));

const Routes = () => {
  const isAutomationsEnabled = useSelector(selectIsAutomationsEnabled);

  return (
    <App>
      <Suspense fallback={<Loader isLoading />}>
        <Switch>
          <GuestRoute exact path={routes.checkEmail} component={CheckInboxContainer} />
          <GuestRoute exact path={routes.linkExpired} component={LinkExpiredContainer} />
          <GuestRoute exact path={routes.verifyMagicLink} component={VerifyMagicLinkContainer} />
          <GuestRoute exact path={routes.login} component={LoginContainer} />
          <Route
            exact
            path={[routes.sfAuthorizeCallback, routes.hubspotAuthorizeCallback, routes.outlookAuthorizeCallback]}
            component={AuthCallback}
          />
          <AdminRoute
            exact
            roles={HUBSPOT_APP_INSTALL_ROLES}
            path={routes.hubspotAppIntegration}
            component={HubspotAppIntegration}
          />
          <PrivateRoute exact path={routes.dashboard} component={Dashboard} />
          <PrivateRoute exact path={routes.createSend.route} component={CreateSendHost} />
          <PrivateRoute exact path={routes.integrations} component={Integrations} />
          <PrivateRoute exact path={routes.inventory.route} component={Inventory} />
          <PrivateRoute exact path={[routes.campaigns.root, routes.campaigns.route]} component={Campaigns} />
          <PrivateRoute exact path={routes.addressBook} component={AddressBook} />
          <PrivateRoute exact path={[routes.reports.root, routes.reports.route]} component={Reports} />
          <PrivateRoute
            exact
            path={[routes.experienceManager.claimLink, routes.experienceManager.claimLinkRoute]}
            component={ClaimLinkTemplatesPage}
          />
          <PrivateRoute
            exact
            path={[routes.experienceManager.emailTemplates, routes.experienceManager.emailTemplatesRoute]}
            component={EmailTemplatesPage}
          />
          <PrivateRoute
            exact
            path={[routes.experienceManager.customMessages, routes.experienceManager.customMessagesRoute]}
            component={CustomMessageTemplatesPage}
          />
          <PrivateRoute exact path={[routes.templates.root, routes.templates.route]} component={Templates} />
          <PrivateRoute exact path={routes.businessReasons} component={BusinessReasons} />
          {isAutomationsEnabled && (
            <AdminRoute
              exact
              roles={[ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN]}
              path={routes.automations}
              component={Automations}
            />
          )}
          <AdminRoute
            exact
            roles={[DEPT_ADMIN, ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN]}
            path={[routes.users.root, routes.users.route]}
            component={Users}
          />
          <AdminRoute
            exact
            roles={[DISTRIBUTOR, SUPER_ADMIN]}
            path={[routes.organizations.root, routes.organizations.route]}
            component={Organizations}
          />
          <AdminRoute
            exact
            roles={[DEPT_ADMIN, ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN]}
            path={[routes.oneLinkManager.root, routes.oneLinkManager.route]}
            component={OneLinkManager}
          />
          <AdminRoute
            exact
            roles={[DEPT_ADMIN, ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN]}
            path={routes.bulkSendManager.root}
            component={BulkSendManager}
          />
          <PrivateRoute exact path={routes.signatureSetup} component={SignatureSetup} />
          {IS_AI_SUGGEST_ENABLED && <PrivateRoute exact path={routes.suggestGift} component={SuggestGift} />}
          <DeadEndRoute />
        </Switch>
      </Suspense>
      <ModalContainer />
    </App>
  );
};

export default Routes;
